import React from "react";
import { useParams } from "react-router-dom";
import {
  fetchProperty,
  fetchPropertyFinance,
  addNewRate,
  deleteMortageRate,
  updateMortageRate,
  fetchPropertyExpense,
  addNewExpense,
  updateExpense,
  deleteExpense,
} from "../api";
import { useQuery, useQueryClient } from "react-query";
import Spinner from "../components/Spinner/Spinner";
import Image from "../components/Image/Image";
import Grid from "../components/Grid/Grid";
import { GlobalContext } from "./../context/GlobalContext";
import moment from "moment";
import Table from "../components/Table/Table";
import formatNumber from "../helpers/formatNumber";
import OpenMap from "../components/OpenMap/OpenMap";
import Charts from "../components/Charts/Charts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import Modal from "../components/Modal/Modal";
import { useNotifications } from "../helpers/useNotifications";
import {
  faCaretDown,
  faCaretUp,
  faSquarePlus,
  faCreditCard,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const columnsMortagePayment = [
  {
    label: "",
    field: "creditName",
    colWidth: "75%",
    rowValue: (_, row) => (
      <div className="dividend-payment-container">
        <div className="dividend-payment-image-container">
          {row.creditLogo ? (
            <img
              src={row.creditLogo}
              alt={row.creditName}
              className="table-thumbnail"
              height={45}
              width={45}
              style={{ marginRight: 8 }}
            />
          ) : (
            <FontAwesomeIcon
              className="table-thumbnail"
              size="lg"
              height={45}
              width={45}
              style={{ marginRight: 8 }}
              icon={faCreditCard}
            />
          )}
        </div>
        <div
          className="dividend-payment-text-container"
          style={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="dividend-payment-date text-muted">
            <span className="trades-share-badge badge badge-info">
              {translation[row.type]}
            </span>
          </div>
          <div className="dividend-payment-name text-light">
            {moment(row?.date).format("DD.MM.YYYY")}
          </div>
        </div>
      </div>
    ),
  },
  {
    label: "",
    field: "totalPayment",
    colWidth: "25%",
    rowValue: (val, row) => {
      return (
        <div className="d-flex align-items-center p-3 justify-content-end">
          <span className="dividend-payment-value  badge badge-success">
            + {formatNumber(val)} €
          </span>
        </div>
      );
    },
  },
];

const columnsExpense = [
  {
    label: "",
    field: "creditName",
    colWidth: "75%",
    rowValue: (_, row) => (
      <div className="dividend-payment-container">
        <div
          className="dividend-payment-text-container"
          style={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="dividend-payment-date text-muted">
            <span className="trades-share-badge badge badge-info">
              {row.company}
            </span>
            <span className="trades-share-badge badge badge-info">
              {moment(row?.date).format("DD.MM.YYYY")}
            </span>
          </div>
          <div className="dividend-payment-name text-light">{row.reason}</div>
        </div>
      </div>
    ),
  },
  {
    label: "",
    field: "value",
    colWidth: "25%",
    rowValue: (val, row) => {
      return (
        <div className="d-flex align-items-center p-3 justify-content-end">
          <span className="badge-negative badge">- {formatNumber(val)} €</span>
        </div>
      );
    },
  },
];

const translation = {
  area: "Grundstück",
  house: "Immobilie",
  sqm: "Fläche (m²)",
  buyprice: "Kaufpreis €",
  flur: "Flurstück",
  flur_number: "Flurstücks-Nr.",
  built_year: "Baujahr",
  rooms: "Zimmer",
  ground_value: "Bodenrichtwert (m²)",
  energy_class: "Energieklasse",
  energy_type: "Heizart",
  heat_pump: "Wärmepumpe",
  gas: "Gas",
  oil: "Öl",
  extra: "Sondertilgung",
  annuity: "Annuität",
  interest: "Zinsen",
  roof: "Dach",
  tent_roof: "Zeltdach",
  gable_roof: "Satteldach",
  flat_roof: "Flachdach",
  roof_pitch: "Dachneigung",
  solar_power_system: "PV-Anlage (kWp)",
  solar_battery_storage: "Batterie Speicher (kWh)",
  equity: "Eigenkapital",
};
const columns = [
  {
    label: "",
    field: "Key",
    colWidth: "50%",
    rowValue: (val, row) => translation[val.toLowerCase()],
  },
  {
    label: "",
    field: "Value",
    colWidth: "50%",
    rowValue: (val, row) => {
      let formattedValue = val;
      if (
        typeof val === "number" &&
        !["flur_number", "built_year"].includes(row?.Key)
      ) {
        formattedValue = formatNumber(val.toFixed(2));
      } else if (
        typeof val === "string" &&
        row?.Key?.toLowerCase().includes("date")
      ) {
        formattedValue = moment(val).format("DD.MM.YYYY hh:mm");
      } else {
        formattedValue = translation[val] || val;
      }

      return <p>{formattedValue}</p>;
    },
  },
];

function PropertyInfo() {
  //* init
  const { zip, id } = useParams();
  const { isMobile } = React.useContext(GlobalContext);
  const queryClient = useQueryClient();
  const createNotification = useNotifications();

  //* data
  const { data, isLoading } = useQuery(
    [`property-info`, id],
    () => fetchProperty(zip, id),
    {
      enabled: !!id,
    },
  );

  const { data: dbFinance } = useQuery(
    ["property-finance", id],
    () => fetchPropertyFinance(id),
    {
      enabled: !!id,
    },
  );

  const { data: dbExpense } = useQuery(
    ["property-expenses", id],
    () => fetchPropertyExpense(id),
    {
      enabled: !!id,
    },
  );

  //* states
  const [showProfitAsPercentage, setShowProfitAsPercentage] =
    React.useState(false);
  const [showMortageDetails, setShowMortageDetails] = React.useState(false);
  const [showDebtDetails, setShowDebtDetails] = React.useState(false);
  const [showRateModal, setShowRateModal] = React.useState(false);
  const [showMortageEdit, setShowMortageEdit] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(undefined);
  const [updatedRateValues, setUpdatedRateValues] = React.useState({
    date: undefined,
    type: undefined,
    value: undefined,
  });

  const [showAllExpenses, setShowAllExpenses] = React.useState(false);
  const [showExpenseModal, setShowExpenseModal] = React.useState(false);
  const [showExpenseEdit, setShowExpenseEdit] = React.useState(false);
  const [updatedExpenseValues, setUpdatedExpenseValues] = React.useState({
    company: undefined,
    date: undefined,
    reason: undefined,
    reference: undefined,
    value: undefined,
  });

  React.useEffect(() => {
    if (selectedRow) {
      setUpdatedRateValues({
        date: selectedRow.date,
        type: selectedRow.type,
        value: selectedRow.totalPayment,
      });

      setUpdatedExpenseValues({
        company: selectedRow.company,
        date: selectedRow.date,
        reason: selectedRow.reason,
        reference: selectedRow.reference,
        value: selectedRow.value,
      });
    }
  }, [selectedRow]);

  //* const
  const maxSizeMortagePayments = 2;
  const totalValue = (data?.area?.value || 0) + (data?.house?.value || 0);
  const totalBuyIn = (data?.area?.buyPrice || 0) + (data?.house?.buyPrice || 0);
  const profit = (totalValue || 0) - (totalBuyIn || 0);
  const performance = (totalValue / totalBuyIn - 1) * 100;
  const lenderOptions = dbFinance?.credits?.map((el, index) => ({
    id: index,
    name: el.name,
  }));

  //* Schulden
  const debt = dbFinance?.summary?.totalLoanAmount;
  const alreadyPayed = dbFinance?.summary?.totalRepaid;
  const openDebt = debt - alreadyPayed;
  const alreadyPayedPercentage = (alreadyPayed / debt) * 100;

  //* Cashflow
  const monthlyCosts =
    data?.financing?.reduce((sum, item) => sum + item.monthly_annuity, 0) || 0;
  const monthlyIncome = 0;
  const cashflow = monthlyIncome - monthlyCosts;

  //* Property data
  const preparedPropertyData = React.useMemo(() => {
    if (!data?.propertyData) return [];

    return Object.entries(data?.propertyData).map(([group, values]) => ({
      group,
      data: Object.entries(values || {})
        .filter(([key]) => key !== "value")
        .map(([key, value]) => ({
          Key: key,
          Value: value,
        })),
    }));
  }, [data?.propertyData]);

  if (isLoading) return <Spinner />;

  return (
    <>
      <div className="article-main">
        <Grid>
          <div
            style={{
              background: "linear-gradient(to right, #343A40, #525252)",
              width: "96vw",
            }}
          >
            <Grid>
              {data?.image && (
                <Image
                  width={100}
                  height={100}
                  source={data?.image}
                  className="table-thumbnail stockinfo-icon"
                />
              )}

              <div
                style={{
                  marginTop: "-20px",
                  marginLeft: "20px",
                  width: "50%",
                  overflow: "hidden",
                }}
              >
                {!isMobile ? (
                  <h5 className="text-light text-truncate">{data.title}</h5>
                ) : (
                  <>
                    <h5 className="text-light text-truncate small-header">{`${data.address.street} ${data.address.house_number}`}</h5>
                    <h5
                      className="text-light text-truncate small-header"
                      style={{ marginTop: "0px" }}
                    >{`${data.address.zip} ${data.address.city}`}</h5>
                  </>
                )}
                <span
                  className="stockinfo-keyfacts-value"
                  style={{
                    fontSize: "14px",
                    color: profit < 0 ? "tomato" : "lawngreen",
                  }}
                  onClick={() => setShowProfitAsPercentage((prev) => !prev)}
                >
                  <FontAwesomeIcon
                    icon={profit < 0 ? faCaretDown : faCaretUp}
                  />
                  &nbsp;
                  {showProfitAsPercentage
                    ? `${formatNumber(performance.toFixed(2))} %`
                    : `${formatNumber(
                        (profit < 0 ? profit * -1 : profit || 0).toFixed(2),
                      )} €`}
                </span>
              </div>
            </Grid>
          </div>

          {/* Personal data */}
          <Grid>
            <div className="col-4 text-center">
              <p className="stockinfo-keyfacts-label text-muted">
                Gesamtwert €
              </p>
              <p className="stockinfo-keyfacts-value text-light small-header">
                {`${formatNumber(totalValue.toFixed(2))}`}
              </p>
            </div>
            <div className="col-4 text-center">
              <p className="stockinfo-keyfacts-label text-muted">Schulden €</p>
              <p className="stockinfo-keyfacts-value text-light small-header">
                {data?.financing?.length > 0
                  ? `${formatNumber(openDebt.toFixed(2))}`
                  : "-"}
              </p>
            </div>
            <div className="col-4 text-center">
              <p className="stockinfo-keyfacts-label text-muted">
                Cashflow (mtl.) €
              </p>
              <p
                className="stockinfo-keyfacts-value small-header"
                style={{
                  color: cashflow < 0 ? "tomato" : "lawngreen",
                }}
              >
                {cashflow ? `${formatNumber(cashflow.toFixed(2))}` : "-"}
              </p>
            </div>
          </Grid>

          {/* Debt */}
          <div className="article-main col-12">
            <h6 className="text-muted" style={{ marginTop: "-10px" }}>
              Schulden{" "}
              <button
                className="btn btn-link dismiss-active-or-focus delete-form-label"
                data-toggle="modal"
                data-target="#debtDetails"
                onClick={(e) => {
                  e.preventDefault();
                  setShowDebtDetails((prev) => !prev);
                }}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </button>
            </h6>
            <div id="meta-description" className={`text-light text-left`}>
              <div
                class="progress"
                role="progressbar"
                aria-label="Default striped example"
                aria-valuenow="10"
                aria-valuemin="0"
                aria-valuemax="100"
                style={{ backgroundColor: "lightgrey" }}
              >
                <div
                  class="progress-bar progress-bar-striped"
                  style={{
                    width: `${alreadyPayedPercentage}%`,
                    backgroundColor: "#9b5cff",
                  }}
                >
                  {alreadyPayedPercentage > 0 &&
                    `${formatNumber(alreadyPayedPercentage)}%`}
                </div>
              </div>
              <br />
              <p>
                Für diese Immobile wurden Schulden in Höhe von{" "}
                <b className="visualizeLinkTwo linkIsInformative">
                  {formatNumber((debt || 0).toFixed(2))}€
                </b>{" "}
                aufgenommen. Insgesamt wurden schon{" "}
                <b className="visualizeLinkTwo linkIsInformative">
                  {formatNumber((alreadyPayed || 0).toFixed(2))}€
                </b>{" "}
                Schulden beglichen und somit bleibt noch eine Restschuld von{" "}
                <b className="visualizeLinkTwo linkIsInformative">
                  {formatNumber((openDebt || 0).toFixed(2))}€
                </b>
                .
              </p>
            </div>
          </div>

          {/* Repayment */}
          <div className="article-main col-12">
            <h6 className="text-muted" style={{ marginBottom: "-10px" }}>
              Tilgung{" "}
              <button
                className="btn btn-link dismiss-active-or-focus delete-form-label"
                data-toggle="modal"
                data-target="#addRate"
                onClick={(e) => {
                  e.preventDefault();
                  setShowRateModal((prev) => !prev);
                }}
              >
                <FontAwesomeIcon icon={faSquarePlus} />
              </button>
            </h6>
            <div id="meta-description" className={`text-light text-left`}>
              <Table
                data={dbFinance?.schedule?.slice(0, maxSizeMortagePayments)}
                columns={columnsMortagePayment}
                id="mortagePayments"
              />

              {dbFinance?.schedule?.length > maxSizeMortagePayments && (
                <div className="text-center mt-6">
                  <LoadingButton
                    onClick={() => setShowMortageDetails((prev) => !prev)}
                    loading={false}
                    title={"Alle anzeigen"}
                  />
                </div>
              )}
              <br />
            </div>
          </div>

          {/* Ownership */}
          <div className="article-main col-12">
            <h6 className="text-muted">Inhaberschaft</h6>
            <div id="meta-description" className={`text-light text-left`}>
              <Grid style={{ padding: 0 }}>
                <p
                  className={`${isMobile ? "col-12" : "col-6"}`}
                  style={{ padding: "0px" }}
                >
                  Dein Anteil an der Immobilie beträgt{" "}
                  <b className="visualizeLinkTwo linkIsInformative">
                    {formatNumber((data?.myShare * 100).toFixed(2))}%
                  </b>{" "}
                  und somit{" "}
                  <b className="visualizeLinkTwo linkIsInformative">
                    {formatNumber((data?.myShare * totalValue).toFixed(2))}€
                  </b>
                  .
                </p>
                <Charts.Pie
                  showActiveKey
                  dataset={[
                    {
                      name: "Mein Anteil",
                      value: data?.myShare * totalValue,
                    },
                    {
                      name: "Restlicher Anteil",
                      value: (1 - data?.myShare) * totalValue,
                    },
                  ]}
                  colors={["#9b5cff", "#d3d3d3"]}
                  startPosition={180}
                  endPosition={0}
                  innerRadius="75%"
                  animated={true}
                  label={`${formatNumber(totalValue)} €`}
                  metaLabel={"Besitz"}
                  className={`pie-chart-ownership-container ${isMobile ? "col-12" : "col-6"}`}
                />
              </Grid>
            </div>
            <br />
            <br />
          </div>

          {/* Expense */}
          <div className="article-main col-12">
            <h6
              className="text-muted d-flex align-items-center justify-content-between"
              style={{ marginBottom: "-10px" }}
            >
              <span>
                Ausgaben{" "}
                <button
                  className="btn btn-link dismiss-active-or-focus delete-form-label"
                  data-toggle="modal"
                  data-target="#addExpense"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowExpenseModal((prev) => !prev);
                  }}
                >
                  <FontAwesomeIcon icon={faSquarePlus} />
                </button>
              </span>

              <span
                className="badge-negative badge"
                style={{ marginRight: "28px" }}
              >
                ∑ - {formatNumber(dbExpense?.totalExpenses)} €
              </span>
            </h6>
            <div id="meta-description" className={`text-light text-left`}>
              <Table
                data={dbExpense?.expenses?.slice(0, maxSizeMortagePayments)}
                columns={columnsExpense}
                id="expenses"
              />

              {dbExpense?.expenses?.length > maxSizeMortagePayments && (
                <div className="text-center mt-6">
                  <LoadingButton
                    onClick={() => setShowAllExpenses((prev) => !prev)}
                    loading={false}
                    title={"Alle anzeigen"}
                  />
                </div>
              )}
              <br />
            </div>
          </div>

          {/* Data */}
          {preparedPropertyData.map((group) => (
            <div key={group.group} className="article-main col-12">
              <h6 className="text-muted">{`Daten - ${translation[group.group]}`}</h6>
              <div id="meta-description" className={`text-light text-left`}>
                <div style={{ marginTop: "-20px" }}>
                  <Table data={group.data} columns={columns} />
                </div>
              </div>
            </div>
          ))}

          {/* OpenMap */}
          {!isLoading && (
            <div className="article-main col-12">
              <h6 className="text-muted">Kartenansicht</h6>
              <OpenMap
                locations={[{ ...data.address.coordinates, name: data.title }]}
              />
            </div>
          )}

          {/* Address */}
          <div className="article-main col-12">
            <div id="meta-description" className={`text-muted text-left`}>
              <p>{`${data.title}, ${data.address.country}`}</p>
            </div>
          </div>
        </Grid>
      </div>

      {/** Add New Data */}
      <Modal
        id="addRate"
        visible={showRateModal}
        setVisible={setShowRateModal}
        title="Zahlung hinzufügen"
        onSave={(formData) => {
          if (
            formData?.lender &&
            formData?.date &&
            formData?.type &&
            formData?.value
          ) {
            addNewRate(
              id,
              formData?.lender,
              formData?.type,
              formData?.date,
              formData?.value,
            )
              .then((result) => {
                createNotification("success", result.message);
                queryClient.invalidateQueries(["property-finance", id]);
                queryClient.invalidateQueries([`property-info`, id]);
              })
              .catch((err) => {
                createNotification("error", err.message);
              });
          } else {
            createNotification("error", "Bitte alle Daten angeben");
          }
        }}
      >
        <div className="form-group col-12">
          <label className="text-dark" for="lender">
            Darlehensgeber
          </label>
          <div className="input-group">
            <select
              className="form-control"
              id="lender"
              name="lender"
              data-live-search="true"
              showContent="true"
            >
              {lenderOptions?.map((el) => (
                <option key={el?.id} value={el?.id}>
                  {el?.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="date">
            Datum
          </label>
          <div className="input-group">
            <input type="date" className="form-control" id="date" name="date" />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="type">
            Typ
          </label>
          <div className="input-group">
            <select
              className="form-control"
              id="type"
              name="type"
              data-live-search="true"
              showContent="true"
            >
              <option key="annuity" value="annuity">
                Rate (Annuität)
              </option>
              <option key="extra" value="extra">
                Sondertilgung
              </option>
              <option key="interest" value="interest">
                Zinsen
              </option>
            </select>
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="value">
            Summe
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="value"
              name="value"
            />
          </div>
        </div>
      </Modal>

      <Modal
        id="addExpense"
        visible={showExpenseModal}
        setVisible={setShowExpenseModal}
        title="Ausgabe hinzufügen"
        onSave={(formData) => {
          if (
            formData?.company &&
            formData?.date &&
            formData?.reason &&
            formData?.reference &&
            formData?.value
          ) {
            addNewExpense(
              id,
              formData?.company,
              formData?.date,
              formData?.reason,
              formData?.reference,
              formData?.value,
            )
              .then((result) => {
                createNotification("success", result.message);
                queryClient.invalidateQueries(["property-expenses", id]);
                queryClient.invalidateQueries([`property-info`, id]);
              })
              .catch((err) => {
                createNotification("error", err.message);
              });
          } else {
            createNotification("error", "Bitte alle Daten angeben");
          }
        }}
      >
        <div className="form-group col-12">
          <label className="text-dark" for="company">
            Firma
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="company"
              name="company"
            />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="date">
            Datum
          </label>
          <div className="input-group">
            <input type="date" className="form-control" id="date" name="date" />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="reason">
            Zweck
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="reason"
              name="reason"
            />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="reference">
            Referenz
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="reference"
              name="reference"
            />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="value">
            Summe
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="value"
              name="value"
            />
          </div>
        </div>
      </Modal>

      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showMortageDetails && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowMortageDetails((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body article-secondary-scroll">
          <div className="article-secondary-info">
            <h5 className="text-dark text-muted">Tilgung</h5>
            <div className="bg-dark-subtle">
              <Table
                data={dbFinance?.schedule}
                columns={columnsMortagePayment}
                id="mortagePayments"
                onClick={(row) => {
                  setShowMortageEdit(true);
                  setSelectedRow(row);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showDebtDetails && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowDebtDetails((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body article-secondary-scroll">
          <div className="article-secondary-info">
            <h5 className="text-dark text-muted">Details</h5>
            <div className="bg-dark-subtle">
              {dbFinance?.credits?.map((el, index, array) => {
                const creditRepaidPercentage =
                  (el.totalRepaid / el.initialAmount) * 100;
                const isLast = index === array.length - 1;
                return (
                  <div id="meta-description" className={`text-light text-left`}>
                    <div className="dividend-payment-container">
                      <div className="dividend-payment-image-container">
                        {el.logo ? (
                          <img
                            src={el.logo}
                            alt={el.name}
                            className="table-thumbnail"
                            height={45}
                            width={45}
                            style={{ marginRight: 8 }}
                          />
                        ) : (
                          <FontAwesomeIcon
                            className="table-thumbnail"
                            size="lg"
                            height={45}
                            width={45}
                            style={{ marginRight: 8 }}
                            icon={faCreditCard}
                          />
                        )}
                      </div>
                      <div
                        className="dividend-payment-text-container"
                        style={{
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <div className="dividend-payment-name text-light">
                          {el.name}
                        </div>
                      </div>
                    </div>
                    <br />

                    <div
                      class="progress"
                      role="progressbar"
                      aria-label="Default striped example"
                      aria-valuenow="10"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ backgroundColor: "lightgrey" }}
                    >
                      <div
                        class="progress-bar progress-bar-striped"
                        style={{
                          width: `${creditRepaidPercentage}%`,
                          backgroundColor: "#9b5cff",
                        }}
                      >
                        {creditRepaidPercentage > 0 &&
                          `${formatNumber(creditRepaidPercentage)}%`}
                      </div>
                    </div>
                    <br />
                    <p>
                      Bei {el.name} wurden Schulden in Höhe von{" "}
                      <b className="visualizeLinkTwo linkIsInformative">
                        {formatNumber((el.initialAmount || 0).toFixed(2))}€
                      </b>{" "}
                      aufgenommen. Insgesamt wurden schon{" "}
                      <b className="visualizeLinkTwo linkIsInformative">
                        {formatNumber((el.totalRepaid || 0).toFixed(2))}€
                      </b>{" "}
                      Schulden beglichen und somit bleibt noch eine Restschuld
                      von{" "}
                      <b className="visualizeLinkTwo linkIsInformative">
                        {formatNumber(
                          (el.currentRemainingBalance || 0).toFixed(2),
                        )}
                        €
                      </b>
                      .
                    </p>
                    {!isLast && (
                      <div
                        style={{ height: "1px", width: "100%", opacity: "0.2" }}
                        className="bg-secondary"
                      />
                    )}
                    <br />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showAllExpenses && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowAllExpenses((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body article-secondary-scroll">
          <div className="article-secondary-info">
            <h5 className="text-dark text-muted">Ausgaben</h5>
            <span className="badge-negative badge">
              ∑ - {formatNumber(dbExpense?.totalExpenses)} €
            </span>
            <div className="bg-dark-subtle">
              <Table
                data={dbExpense?.expenses}
                columns={columnsExpense}
                id="expenses"
                onClick={(row) => {
                  setShowExpenseEdit(true);
                  setSelectedRow(row);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/** Edit-Mode */}
      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showMortageEdit && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowMortageEdit((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body article-secondary-scroll">
          <div className="article-secondary-info">
            {selectedRow?.creditLogo ? (
              <Image
                source={selectedRow?.creditLogo}
                className="article-logo"
              />
            ) : (
              <FontAwesomeIcon
                className="table-thumbnail"
                size="lg"
                height={45}
                width={45}
                style={{ marginRight: 8 }}
                icon={faCreditCard}
              />
            )}

            <h4 className="text-dark text-muted text-truncate">
              {selectedRow?.creditName}
            </h4>

            <div
              id="data-description"
              className="text-muted text-left article-base-description-active"
            >
              <form id="updateRate">
                <div className="form-group col-12">
                  <label className="text-dark" for="date">
                    Datum
                  </label>
                  <div className="input-group">
                    <input
                      type="date"
                      className="form-control"
                      id="date"
                      name="date"
                      value={updatedRateValues.date}
                      onChange={(e) =>
                        setUpdatedRateValues((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="type">
                    Typ
                  </label>
                  <div className="input-group">
                    <select
                      className="form-control"
                      id="type"
                      name="type"
                      data-live-search="true"
                      showContent="true"
                      value={updatedRateValues.type}
                      onChange={(e) =>
                        setUpdatedRateValues((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                    >
                      <option key="annuity" value="annuity">
                        Rate (Annuität)
                      </option>
                      <option key="extra" value="extra">
                        Sondertilgung
                      </option>
                      <option key="interest" value="interest">
                        Zinsen
                      </option>
                    </select>
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="value">
                    Summe
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="value"
                      name="value"
                      value={updatedRateValues.value}
                      onChange={(e) =>
                        setUpdatedRateValues((prev) => ({
                          ...prev,
                          value: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div style={{ marginLeft: "15px" }}>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    style={{ marginRight: "5px" }}
                    onClick={() => {
                      deleteMortageRate(
                        id,
                        selectedRow?.creditId,
                        selectedRow?.paymentId,
                      )
                        .then((result) => {
                          createNotification("success", result.message);
                          queryClient.invalidateQueries([
                            "property-finance",
                            id,
                          ]);
                          queryClient.invalidateQueries([`property-info`, id]);
                          setShowMortageEdit(false);
                        })
                        .catch((err) => {
                          createNotification("error", err.message);
                        });
                    }}
                  >
                    Löschen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-search-custom dismiss-active-or-focus"
                    onClick={(e) => {
                      e.preventDefault();

                      if (
                        updatedRateValues.date &&
                        updatedRateValues.type &&
                        updatedRateValues.value
                      ) {
                        updateMortageRate(
                          id,
                          selectedRow?.creditId,
                          selectedRow?.paymentId,
                          updatedRateValues.date,
                          updatedRateValues.type,
                          updatedRateValues.value,
                        )
                          .then((result) => {
                            createNotification("success", result.message);
                            queryClient.invalidateQueries([
                              "property-finance",
                              id,
                            ]);
                            queryClient.invalidateQueries([
                              `property-info`,
                              id,
                            ]);
                            setShowMortageEdit(false);
                          })
                          .catch((err) => {
                            createNotification("error", err.message);
                          });
                      } else {
                        createNotification("error", "Bitte alle Daten angeben");
                      }
                    }}
                  >
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showExpenseEdit && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowExpenseEdit((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body article-secondary-scroll">
          <div className="article-secondary-info">
            <h4 className="text-dark text-muted text-truncate">Ausgabe</h4>

            <div
              id="data-description"
              className="text-muted text-left article-base-description-active"
            >
              <form id="updateExpense">
                <div className="form-group col-12">
                  <label className="text-dark" for="company">
                    Firma
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="company"
                      name="company"
                      value={updatedExpenseValues.company}
                      onChange={(e) =>
                        setUpdatedExpenseValues((prev) => ({
                          ...prev,
                          company: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="form-group col-12">
                  <label className="text-dark" for="date">
                    Datum
                  </label>
                  <div className="input-group">
                    <input
                      type="date"
                      className="form-control"
                      id="date"
                      name="date"
                      value={updatedExpenseValues.date}
                      onChange={(e) =>
                        setUpdatedExpenseValues((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="form-group col-12">
                  <label className="text-dark" for="reason">
                    Zweck
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="reason"
                      name="reason"
                      value={updatedExpenseValues.reason}
                      onChange={(e) =>
                        setUpdatedExpenseValues((prev) => ({
                          ...prev,
                          reason: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="form-group col-12">
                  <label className="text-dark" for="reference">
                    Referenz
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="reference"
                      name="reference"
                      value={updatedExpenseValues.reference}
                      onChange={(e) =>
                        setUpdatedExpenseValues((prev) => ({
                          ...prev,
                          reference: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="form-group col-12">
                  <label className="text-dark" for="value">
                    Summe
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="value"
                      name="value"
                      value={updatedExpenseValues.value}
                      onChange={(e) =>
                        setUpdatedExpenseValues((prev) => ({
                          ...prev,
                          value: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div style={{ marginLeft: "15px" }}>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    style={{ marginRight: "5px" }}
                    onClick={() => {
                      deleteExpense(id, selectedRow?.expenseId)
                        .then((result) => {
                          createNotification("success", result.message);
                          queryClient.invalidateQueries([
                            "property-expenses",
                            id,
                          ]);
                          queryClient.invalidateQueries([`property-info`, id]);
                          setShowExpenseEdit(false);
                        })
                        .catch((err) => {
                          createNotification("error", err.message);
                        });
                    }}
                  >
                    Löschen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-search-custom dismiss-active-or-focus"
                    onClick={(e) => {
                      e.preventDefault();

                      if (
                        updatedExpenseValues.company &&
                        updatedExpenseValues.date &&
                        updatedExpenseValues.reason &&
                        updatedExpenseValues.reference &&
                        updatedExpenseValues.value
                      ) {
                        updateExpense(
                          id,
                          selectedRow?.expenseId,
                          updatedExpenseValues.company,
                          updatedExpenseValues.date,
                          updatedExpenseValues.reason,
                          updatedExpenseValues.reference,
                          updatedExpenseValues.value,
                        )
                          .then((result) => {
                            createNotification("success", result.message);
                            queryClient.invalidateQueries([
                              "property-expenses",
                              id,
                            ]);
                            queryClient.invalidateQueries([
                              `property-info`,
                              id,
                            ]);
                            setShowExpenseEdit(false);
                          })
                          .catch((err) => {
                            createNotification("error", err.message);
                          });
                      } else {
                        createNotification("error", "Bitte alle Daten angeben");
                      }
                    }}
                  >
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PropertyInfo;
